// Logique cartographique ROBUSTE PARTAGÉE (Live Tracking + Historique) — source unique, pas de divergence.
// Fonds mondiaux fiables + Géorép NC (région "nc"), overzoom Leaflet, fallback anti-boucle sur tileerror,
// désactivation automatique de Géorép hors NC (ex. Tahiti) avec notice discrète.
import { useEffect, useRef, useState } from "react";
import { useMap, useMapEvents, TileLayer } from "react-leaflet";
import { toast } from "sonner";

// region: world = mondial fiable ; nc = spécifique Nouvelle-Calédonie (Géorép).
export const TILES = {
  dark: { label: "Sombre", region: "world", maxNativeZoom: 16, url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: "© Esri" },
  light: { label: "Clair", region: "world", maxNativeZoom: 16, url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: "© Esri" },
  osm: { label: "OSM", region: "world", maxNativeZoom: 19, url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "© OpenStreetMap" },
  satellite: { label: "Satellite", region: "world", maxNativeZoom: 18, url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "© Esri" },
  hybride: { label: "Satellite+", region: "world", maxNativeZoom: 18, url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", overlays: ["https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}", "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"], attribution: "© Esri" },
  georep: { label: "Géorép", region: "nc", maxNativeZoom: 18, url: "https://tiles.arcgis.com/tiles/TZcrgU6CIbqWt9Qv/arcgis/rest/services/fond_cartographie/MapServer/tile/{z}/{y}/{x}", attribution: "© Géorép NC" },
  georep_sat: { label: "Géorép Ortho", region: "nc", maxNativeZoom: 19, url: "https://tiles.arcgis.com/tiles/TZcrgU6CIbqWt9Qv/arcgis/rest/services/fond_imagerie/MapServer/tile/{z}/{y}/{x}", attribution: "© Géorép NC" },
};
export const MAP_MAX_ZOOM = 20;
// Zoom natif effectif d'un fond (sécurité si clé absente). Au-delà, Leaflet AGRANDIT la dernière
// tuile native (overzoom) au lieu de charger des tuiles inexistantes qui renvoient du blanc.
export const layerMaxNative = (layer) => (TILES[layer] && TILES[layer].maxNativeZoom) || 18;
export const NC_BBOX = { latMin: -23.5, latMax: -19.0, lonMin: 162.5, lonMax: 169.0 };
export const inNC = (lat, lon) => lat >= NC_BBOX.latMin && lat <= NC_BBOX.latMax && lon >= NC_BBOX.lonMin && lon <= NC_BBOX.lonMax;

// Gestion territoire + fallback tuiles : jamais d'écran gris, jamais de boucle. onTerritory optionnel.
export function TileManager({ layer, setLayer, onTerritory }) {
  const map = useMap();
  const errRef = useRef({ layer, count: 0, ts: 0, fellBack: false });
  useEffect(() => { errRef.current = { layer, count: 0, ts: 0, fellBack: false }; }, [layer]);
  useMapEvents({
    moveend() {
      const c = map.getCenter();
      const terr = inNC(c.lat, c.lng) ? "nc" : "world";
      if (onTerritory) onTerritory(terr);
      if (terr !== "nc" && TILES[layer]?.region === "nc") {
        setLayer("satellite");
        toast.message("Fond régional (Géorép NC) indisponible ici — Satellite mondial affiché");
      }
    },
    tileerror() {
      const now = Date.now(); const e = errRef.current;
      if (now - e.ts > 4000) { e.count = 0; e.ts = now; }
      e.count += 1;
      if (e.count >= 8 && !e.fellBack) {
        e.fellBack = true;
        const fb = layer === "satellite" ? "osm" : "satellite";
        toast.message(`Fond « ${TILES[layer]?.label} » indisponible à ce niveau — carte standard affichée`);
        setLayer(fb);
      }
    },
  });
  return null;
}

// Fond de carte + overlays éventuels (ex. "Satellite+" = imagerie + noms de rues/routes transparents).
// Base en zIndex 1, overlays au-dessus mais sous les marqueurs (markerPane ~600).
export function BaseTileLayer({ layer }) {
  const t = TILES[layer] || TILES.satellite;
  const mn = layerMaxNative(layer);
  return (
    <>
      <TileLayer key={layer} url={t.url} attribution={t.attribution} maxZoom={MAP_MAX_ZOOM} maxNativeZoom={mn} zIndex={1} />
      {(t.overlays || []).map((u, i) => (
        <TileLayer key={`${layer}-ov-${i}`} url={u} maxZoom={MAP_MAX_ZOOM} maxNativeZoom={mn} zIndex={10 + i} />
      ))}
    </>
  );
}

// Indicateur de zoom (overlay discret) : niveau courant + note quand on dépasse le zoom natif du fond
// (image agrandie, jamais d'écran blanc). N'altère pas les marqueurs/contrôles.
export function ZoomInfo({ layer, position = "bottomleft" }) {
  const map = useMap();
  const [z, setZ] = useState(() => Math.round(map.getZoom()));
  useMapEvents({ zoomend() { setZ(Math.round(map.getZoom())); }, zoom() { setZ(Math.round(map.getZoom())); } });
  const mn = layerMaxNative(layer);
  const atMax = z >= MAP_MAX_ZOOM;
  const over = z > mn;
  const corner = position === "bottomright" ? "leaflet-bottom leaflet-right" : "leaflet-bottom leaflet-left";
  const margin = position === "bottomright" ? "0 10px 62px 0" : "0 0 10px 10px";
  return (
    <div className={corner} style={{ pointerEvents: "none" }}>
      <div className="leaflet-control" style={{ margin, pointerEvents: "none" }}>
        <div data-testid="map-zoom-indicator" className="flex items-center gap-1.5 rounded-full bg-black/65 text-white text-[11px] font-semibold px-2.5 py-1 shadow backdrop-blur-sm whitespace-nowrap">
          <span>Zoom {z}/{MAP_MAX_ZOOM}{atMax ? " · max" : ""}</span>
          {over && <span data-testid="map-zoom-overzoom" className="text-[#F5C36B]">· Zoom maximal pour ce fond</span>}
        </div>
      </div>
    </div>
  );
}
